import React, { useEffect, useState } from "react";
import { parse, format } from "date-fns";
import PrimaryButton from "../buttons/PrimaryButton";
import Pagination from "../pagination/Pagination";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import axios from "axios";
import Cookies from "js-cookie";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { Icon } from '@iconify/react/dist/iconify.js';
import Modal from "../modal/Modal";
import confirmIcon from "../../assets/img/iconamoon--shield-yes-fill.svg";
import PageSizeSelector from "../pagination/PageSizeSelector";
import usePaginatedData from "../pagination/hooks/usePaginatedData";
import DatePickerComponent from "../datepicker/DatePicker";

const CoordinatorApproveDataTable = ({
  activeTab,
  districtSearchQuery,
  sectorSearchQuery,
  operatorSearchQuery,
  bookTypeFilter,
  startDate,
  endDate,
}) => {
  // Configuration for endpoints and transform functions
  const endpoints = {
    "scanned-records": `/api/scanned/scanned-records/archives`,
    "yellow-forms": `/api/yellow-forms/archives/scanned/yellow-forms`
  };

  // Use the pagination hook
  const {
    data: filteredData,
    pagination,
    loading,
    error: fetchError,
    goToPage,
    changePageSize,
    refresh
  } = usePaginatedData({
    activeTab,
    districtSearchQuery,
    sectorSearchQuery,
    operatorSearchQuery,
    bookTypeFilter,
    startDate,
    endDate,
    endpoints,
    // Using default transform functions from the hook
    transformFunctions: null
  });

  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showActionConfirm, setShowActionConfirm] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [recordsToProcess, setRecordsToProcess] = useState([]);

  // Edit-record modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({
    bookNumber: "",
    category: "",
    type: "",
    count: "",
    scanDate: new Date(),
  });
  const [availableBooks, setAvailableBooks] = useState([]);
  const [uniqueBookCategories, setUniqueBookCategories] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [fetchingBooks, setFetchingBooks] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const role = Cookies.get("role");

  const capitalize = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  useEffect(() => {
    let timer;
    if (showSuccess) {
      timer = setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccess]);

  // Reset selected records when page changes or data updates
  useEffect(() => {
    setSelectedRecords([]);
    setIsAllSelected(false);
  }, [pagination.currentPage, activeTab, filteredData]);

  const getRecordDetails = (recordId) => {
    return filteredData.find((item) => item.id === recordId) || {};
  };

  const fetchBookData = async () => {
    try {
      setFetchingBooks(true);
      const token = Cookies.get("token");
      const response = await axios.get(`${baseUrl}/api/books`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const filteredBooks = response.data.filter(
        (book) => book.name !== "Yellow Forms"
      );
      setAvailableBooks(filteredBooks);
      setUniqueBookCategories([...new Set(filteredBooks.map((book) => book.name))]);
    } catch (error) {
      setEditError("Error fetching book categories");
    } finally {
      setFetchingBooks(false);
    }
  };

  useEffect(() => {
    if (showEditModal) {
      fetchBookData();
    }
  }, [showEditModal]);

  // Keep the Book Type options in sync with whichever category is selected
  useEffect(() => {
    if (editFormData.category && availableBooks.length > 0) {
      const types = availableBooks
        .filter((book) => book.name === editFormData.category)
        .map((book) => book.bookType);
      setAvailableTypes(types);
    }
  }, [editFormData.category, availableBooks]);

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditFormData({
      bookNumber: record.bookNumber === "-" ? "" : record.bookNumber,
      category: record.bookName || "",
      type: record.bookType ? record.bookType.toUpperCase() : "",
      count: record.count,
      scanDate: record.scanDate ? new Date(record.scanDate) : new Date(),
    });
    setEditError("");
    setShowEditModal(true);
  };

  const handleEditCategoryChange = (e) => {
    setEditFormData((prev) => ({
      ...prev,
      category: e.target.value,
      type: "",
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditDateChange = (date) => {
    setEditFormData((prev) => ({ ...prev, scanDate: date }));
  };

  const handleEditSubmitClick = () => {
    setEditError("");
    if (
      !editFormData.category ||
      !editFormData.type ||
      !editFormData.bookNumber ||
      editFormData.count === ""
    ) {
      setEditError("Please fill in all required fields");
      return;
    }
    if (parseInt(editFormData.count, 10) <= 0) {
      setEditError("Estimated number of records must be greater than 0");
      return;
    }
    setShowEditConfirm(true);
  };

  const handleConfirmEditSubmit = async () => {
    setEditSubmitting(true);
    setEditError("");
    try {
      const selectedBook = availableBooks.find(
        (book) =>
          book.name === editFormData.category &&
          book.bookType === editFormData.type
      );

      if (!selectedBook) {
        setEditError("Selected book category/type combination was not found");
        setEditSubmitting(false);
        return;
      }

      const token = Cookies.get("token");
      await axios.put(
        `${baseUrl}/api/scanned/scanned-records/archives/${editingRecord.id}`,
        {
          count: parseInt(editFormData.count, 10),
          bookNumber: editFormData.bookNumber,
          bookId: selectedBook.id,
          scanDate: editFormData.scanDate.toISOString(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowEditConfirm(false);
      setShowEditModal(false);
      setSuccessMessage("Record has been updated successfully!");
      setShowSuccess(true);
      refresh();
    } catch (error) {
      setShowEditConfirm(false);
      setEditError(error.response?.data?.error || "Error updating record");
    } finally {
      setEditSubmitting(false);
    }
  };

  const actionDisabled = (status) =>
    status === "APPROVED" || status === "REJECTED";

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setIsAllSelected(isChecked);
    if (isChecked) {
      setSelectedRecords(
        filteredData
          .filter((item) => !actionDisabled(item.status))
          .map((item) => item.id)
      );
    } else {
      setSelectedRecords([]);
    }
  };

  const handleSelectRecord = (e, recordId) => {
    const isChecked = e.target.checked;
    setSelectedRecords((prev) =>
      isChecked ? [...prev, recordId] : prev.filter((id) => id !== recordId)
    );
    
    const availableForSelection = filteredData.filter((item) => !actionDisabled(item.status));
    const newSelectedCount = isChecked 
      ? selectedRecords.length + 1 
      : selectedRecords.length - 1;
    
    setIsAllSelected(newSelectedCount === availableForSelection.length);
  };

  const handleActionConfirm = (recordIds, action) => {
    setRecordsToProcess(Array.isArray(recordIds) ? recordIds : [recordIds]);
    setActionType(action);
    setShowActionConfirm(true);
  };

  const handleProcessRecords = async () => {
    if (!recordsToProcess.length) return;
    setSubmitting(true);
    const isYellowForm = activeTab === "yellow-forms";
    const isMultiple = recordsToProcess.length > 1;
    
    try {
      const token = Cookies.get("token");
      let endpoint;
      let payload;

      if (actionType === "RESET") {
        if (recordsToProcess.length > 1) {
          throw new Error("Reset action can only be performed on a single record");
        }
        endpoint = isYellowForm 
          ? `/api/yellow-forms/archives/${recordsToProcess[0]}/reset`
          : `/api/scanned/scanned-records/archives/${recordsToProcess[0]}/reset`;
        payload = isYellowForm ? { formType: "SCANNED" } : {};
      } else {
        endpoint = isYellowForm
          ? actionType === "APPROVED"
            ? `/api/yellow-forms/archives/approve/multiple`
            : `/api/yellow-forms/archives/reject/multiple`
          : actionType === "APPROVED"
          ? `/api/scanned/scanned-records/archives/approve-multiple`
          : `/api/scanned/scanned-records/archives/reject-multiple`;
        payload = isYellowForm
          ? { recordIds: recordsToProcess, formType: "SCANNED" }
          : { recordIds: recordsToProcess };
      }

      const response = await axios.post(
        `${baseUrl}${endpoint}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        setSuccessMessage(
          actionType === "RESET"
            ? "Record has been successfully reset to pending!"
            : isMultiple
              ? `Records have been successfully ${
                  actionType === "APPROVED" ? "approved" : "declined"
                }!`
              : `Record has been successfully ${
                  actionType === "APPROVED" ? "approved" : "declined"
                }!`
        );
        setShowActionConfirm(false);
        setShowSuccess(true);
        setSelectedRecords([]);
        setIsAllSelected(false);
        refresh();
      }
    } catch (error) {
      setError(
        `Error ${
          actionType === "APPROVED" ? "approving" : actionType === "REJECTED" ? "declining" : "resetting"
        } record(s): ${error.message}`
      );
      console.error("Processing error:", error);
    } finally {
      setSubmitting(false);
      setRecordsToProcess([]);
    }
  };

  const handlePageChange = (newPage) => {
    goToPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value);
    changePageSize(newPageSize);
  };

  // Table configuration
  const scannedRecordsColumns = [
    "Province",
    "District",
    "Sector",
    "Book Name",
    "Book Number",
    "Book Type",
    "Estimated № of Records",
    "Scanner Operator",
    "Status",
    "Scanned At",
    "Recorded At",
    ...(role === "PROJECT_COORDINATOR" ? ["Action"] : []),
  ];

  const yellowFormsColumns = [
    "Province",
    "District",
    "Sector",
    "Yellow Form Count",
    "Scanner Operator",
    "Status",
    "Scanned At",
    "Recorded At",
    ...(role === "PROJECT_COORDINATOR" ? ["Action"] : []),
  ];

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const parsedData = parse(dateString, "dd-MM-yyyy", new Date());
    return format(parsedData, "dd MMM yyyy");
  };

  const formatRecordedDate = (dateString) => {
    const parsedData = parse(dateString, "dd-MM-yyyy HH:mm:ss", new Date());
    return format(parsedData, "dd MMM yyyy h:mm a");
  };

  const formatStatus = (status) => {
    if (!status) return "";
    if (status.toUpperCase() === "REJECTED") return "Declined";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const getStatusClass = (status) => {
    const statusMap = {
      APPROVED: "status-approved",
      REJECTED: "status-declined",
      PENDING: "status-pending",
    };
    return `status ${statusMap[status]}`;
  };

  return (
    <>
      {/* Page Size Selector */}
      <div className="table-header-controls">
        <PageSizeSelector
          value={pagination.itemsPerPage}
          onChange={handlePageSizeChange}
          disabled={loading}
        />
        
        {/* Show error message if fetch failed */}
        {fetchError && (
          <div className="error-message">
            <span style={{ color: "#E20000", marginRight: "10px" }}>
              Error: {fetchError}
            </span>
            <button 
              onClick={refresh} 
              className="retry-button"
              disabled={loading}
            >
              <Icon icon="iconamoon:restart" width="16" height="16" />
              Retry
            </button>
          </div>
        )}
      </div>

      {selectedRecords.length > 0 && (
        <div
          className="table-controls"
          style={{ marginBottom: "20px", marginRight: "20px" }}
        >
          <PrimaryButton
            text={`Approve ${selectedRecords.length} Record${
              selectedRecords.length > 1 ? "s" : ""
            }`}
            icon="dashicons:yes"
            onClick={() => handleActionConfirm(selectedRecords, "APPROVED")}
            disabled={submitting}
            style={{ backgroundColor: "#078ECE", color: "#FFFFFF" }}
          />
          <PrimaryButton
            text={`Decline ${selectedRecords.length} Record${
              selectedRecords.length > 1 ? "s" : ""
            }`}
            icon="dashicons:no"
            onClick={() => handleActionConfirm(selectedRecords, "REJECTED")}
            disabled={submitting}
            style={{
              backgroundColor: "#E2000033",
              color: "#E20000",
              marginLeft: "20px",
              marginTop: "10px",
            }}
          />
        </div>
      )}
      
      <table>
        <thead>
          <tr style={{ textTransform: "uppercase" }}>
            {role === "PROJECT_COORDINATOR" && (
              <th>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  disabled={loading}
                />
              </th>
            )}

            {(activeTab === "scanned-records"
              ? scannedRecordsColumns
              : yellowFormsColumns
            ).map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <tr key={index}>
                {role === "PROJECT_COORDINATOR" && (
                  <td>
                    <Skeleton width={20} height={20} />
                  </td>
                )}
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={150} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={80} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={80} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
              </tr>
            ))
          ) : filteredData.length === 0 ? (
            <tr style={{ boxShadow: "none" }}>
              <td
                colSpan="11"
                style={{ textAlign: "center", color: "#9B9B9B" }}
              >
                No data found
              </td>
            </tr>
          ) : (
            filteredData.map((item, index) => (
              <tr key={item.id}>
                {role === "PROJECT_COORDINATOR" && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRecords.includes(item.id)}
                      onChange={(e) => handleSelectRecord(e, item.id)}
                      disabled={actionDisabled(item.status)}
                    />
                  </td>
                )}

                <td>{item.province}</td>
                <td>{item.district}</td>
                <td>{item.sector}</td>
                {activeTab === "scanned-records" ? (
                  <>
                    <td>
                      {item.bookName
                        ? item.bookName === "Special_category"
                          ? "Special Category"
                          : item.bookName
                        : "-"}
                    </td>
                    <td>{item.bookNumber}</td>
                    <td>{item.bookType}</td>
                    <td>{item.count}</td>
                  </>
                ) : (
                  <td>{item.yellowFormCount}</td>
                )}
                <td>{item.scannerOperator}</td>
                <td>
                  <span className={getStatusClass(item.status)}>
                    {formatStatus(item.status)}
                  </span>
                </td>
                <td>{formatDate(item.recordedAt)}</td>
                <td>{formatRecordedDate(item.addedAt)}</td>
                {role === "PROJECT_COORDINATOR" && (
                  <td>
                    <div className="action-buttons">
                      {item.status === "PENDING" ? (
                        <>
                          <PrimaryButton
                            text="Approve"
                            icon="dashicons:yes"
                            onClick={() => handleActionConfirm(item.id, "APPROVED")}
                            disabled={submitting}
                          />
                          <PrimaryButton
                            text="Decline"
                            onClick={() => handleActionConfirm(item.id, "REJECTED")}
                            disabled={submitting}
                          />
                        </>
                      ) : (
                        <PrimaryButton
                          text={
                            submitting && recordsToProcess.includes(item.id) ? (
                              <AiOutlineLoading3Quarters className="loader" />
                            ) : (
                              "Reset"
                            )
                          }
                          icon="material-symbols:restart-alt"
                          onClick={() => handleActionConfirm(item.id, "RESET")}
                          disabled={submitting && recordsToProcess.includes(item.id)}
                          className="full-width"
                          style={{
                            width: "100%",
                            justifyContent: "center",
                            backgroundColor:
                              submitting && recordsToProcess.includes(item.id)
                                ? "#cccccc"
                                : "#E6F4EA",
                            border: "1px solid rgb(0, 112, 17)",
                            color:
                              submitting && recordsToProcess.includes(item.id)
                                ? "#666666"
                                : "#007011",
                            minWidth: 110,
                            cursor:
                              submitting && recordsToProcess.includes(item.id)
                                ? "not-allowed"
                                : "pointer",
                          }}
                        />
                      )}
                      {activeTab === "scanned-records" && (
                        <a
                          onClick={() => handleEditClick(item)}
                          aria-label="Edit"
                          className="icon-button edit-button"
                          style={{ marginLeft: 8 }}
                        >
                          <Icon
                            icon="mdi:book-edit"
                            width="20"
                            height="20"
                            color="#FFAF2F"
                          />
                        </a>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>

      <Modal
        show={showActionConfirm}
        onClose={() => setShowActionConfirm(false)}
      >
        <div className="content confirmation">
          <h2>
            {actionType === "APPROVED"
              ? "Confirm Approval"
              : actionType === "REJECTED"
              ? "Confirm Rejection"
              : "Confirm Reset"}
          </h2>
          <div className="text">
            {recordsToProcess.length > 1 ? (
              <>
                Are you sure you want to{" "}
                {actionType === "APPROVED" ? "approve" : actionType === "REJECTED" ? "decline" : "reset"} the
                following {recordsToProcess.length} records?
                <ul style={{ listStyleType: "none", padding: "10px 0" }}>
                  {recordsToProcess.map((id) => {
                    const record = getRecordDetails(id);
                    return (
                      <li key={id}>
                        {activeTab === "scanned-records"
                          ? `${record.count} scanned records for ${
                              record.bookName
                            } in ${record.sector || ""}, ${
                              record.district || ""
                            }, ${record.province || ""}`
                          : `${record.yellowFormCount} yellow forms in ${
                              record.sector || ""
                            }, ${record.district || ""}, ${
                              record.province || ""
                            }`}
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : recordsToProcess.length === 1 ? (
              <>
                Are you sure you want to{" "}
                {actionType === "APPROVED" ? "approve" : actionType === "REJECTED" ? "decline" : "reset"}{" "}
                {activeTab === "scanned-records"
                  ? `${
                      getRecordDetails(recordsToProcess[0]).count
                    } scanned records for ${
                      getRecordDetails(recordsToProcess[0]).bookName
                    } in ${
                      getRecordDetails(recordsToProcess[0]).sector || ""
                    }, ${
                      getRecordDetails(recordsToProcess[0]).district || ""
                    }, ${
                      getRecordDetails(recordsToProcess[0]).province ||
                      "Unknown"
                    }`
                  : `${
                      getRecordDetails(recordsToProcess[0]).yellowFormCount
                    } yellow forms in ${
                      getRecordDetails(recordsToProcess[0]).sector || ""
                    }, ${
                      getRecordDetails(recordsToProcess[0]).district || ""
                    }, ${
                      getRecordDetails(recordsToProcess[0]).province ||
                      "Unknown"
                    }`}
                ?
              </>
            ) : (
              "No records selected for processing."
            )}
          </div>
          <div className="button-group">
            <PrimaryButton
              text={
                submitting ? (
                  <AiOutlineLoading3Quarters className="loader" />
                ) : (
                  "Yes"
                )
              }
              onClick={handleProcessRecords}
              disabled={submitting}
            />
            <PrimaryButton
              text="No"
              onClick={() => setShowActionConfirm(false)}
              disabled={submitting}
            />
          </div>
        </div>
      </Modal>

      <Modal show={showSuccess} onClose={() => setShowSuccess(false)}>
        <div className="content confirmation">
          <div className="icon-container">
            <img src={confirmIcon} alt="confirm" width="57" height="57" />
          </div>
          <div className="text">{successMessage}</div>
        </div>
      </Modal>

      <Modal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        confirm={true}
      >
        <div className="title initialized">EDIT SCANNED RECORD</div>
        <div className="content">
          <div className="form-container new-records">
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="scanned-books initialized">
                <div className="address">
                  <div className="address-container">
                    <div className="address-text">
                      Province: {editingRecord?.province}
                    </div>
                  </div>
                  <div className="address-container">
                    <div className="address-text">
                      District: {editingRecord?.district}
                    </div>
                  </div>
                  {editingRecord?.sector && editingRecord.sector !== "-" && (
                    <div className="address-container">
                      <div className="address-text">
                        Sector: {editingRecord.sector}
                      </div>
                    </div>
                  )}
                </div>
                <div className="data">
                  <div className="books-container">
                    <div className="date-container">
                      <div className="date-heading">Scan Date</div>
                      <DatePickerComponent
                        selectedDate={editFormData.scanDate}
                        onDateChange={handleEditDateChange}
                      />
                    </div>
                    <div className="book-type-section">
                      <div className="number-section">
                        <div className="type-heading">Book Number</div>
                        <input
                          className="input-field"
                          placeholder="Enter Book Number"
                          type="text"
                          name="bookNumber"
                          value={editFormData.bookNumber}
                          onChange={handleEditInputChange}
                        />
                      </div>
                      <div className="category">
                        <div className="type-heading">Book Category</div>
                        <div className="label">
                          <select
                            name="category"
                            value={editFormData.category}
                            onChange={handleEditCategoryChange}
                            className="form-select"
                            disabled={fetchingBooks}
                          >
                            <option value="" disabled>
                              Select Book category
                            </option>
                            {uniqueBookCategories.map((category) => (
                              <option key={category} value={category}>
                                {category === "Special_category"
                                  ? "Special Category"
                                  : category}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="book-category-section">
                      <div className="type-heading">Book Type</div>
                      <div className="book-category-options">
                        {availableTypes.map((type) => (
                          <label key={type} className="radio-label">
                            <input
                              type="radio"
                              name="type"
                              value={type}
                              checked={editFormData.type === type}
                              onChange={handleEditInputChange}
                            />
                            <span>{capitalize(type)}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="estimated-records-section">
                      <span className="type-heading">
                        Estimated Number of Records
                      </span>
                      <input
                        className="input-field"
                        placeholder="Number of Records"
                        type="number"
                        name="count"
                        value={editFormData.count}
                        onChange={handleEditInputChange}
                        min="1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          {editError && <div className="error-message">{editError}</div>}
          <div className="button-group">
            <PrimaryButton
              text={
                editSubmitting ? (
                  <AiOutlineLoading3Quarters className="loader" />
                ) : (
                  "Save Changes"
                )
              }
              onClick={handleEditSubmitClick}
              disabled={editSubmitting || fetchingBooks}
            />
            <PrimaryButton
              text="Cancel"
              onClick={() => setShowEditModal(false)}
              disabled={editSubmitting}
            />
          </div>
        </div>
      </Modal>

      <Modal
        show={showEditConfirm}
        onClose={() => setShowEditConfirm(false)}
      >
        <div className="content confirmation">
          <h2>Confirm Edit</h2>
          <div className="text">
            Are you sure you want to save these changes
            {editingRecord?.status === "APPROVED"
              ? " to this already-approved record"
              : editingRecord?.status === "REJECTED"
              ? " to this declined record"
              : ""}
            ?
          </div>
          <div className="button-group">
            <PrimaryButton
              text={
                editSubmitting ? (
                  <AiOutlineLoading3Quarters className="loader" />
                ) : (
                  "Yes"
                )
              }
              onClick={handleConfirmEditSubmit}
              disabled={editSubmitting}
            />
            <PrimaryButton
              text="No"
              onClick={() => setShowEditConfirm(false)}
              disabled={editSubmitting}
            />
          </div>
        </div>
      </Modal>

      {/* Entry count is always shown; page navigation only when multi-page */}
      {!loading && (
        <Pagination
          currentPage={pagination.currentPage}
          totalItems={pagination.totalItems}
          entriesPerPage={pagination.itemsPerPage}
          onPageChange={handlePageChange}
          hideNavigation={pagination.totalPages <= 1}
        />
      )}
    </>
  );
};

export default CoordinatorApproveDataTable;